import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { resolveTimezone } from "../../api/directory";
import { useBranchAddress } from "./useBranchAddress";
import { useGeolocation } from "../../hooks/useGeolocation";
import { useMyCompany } from "../../api/useMyCompany";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { TabPanel } from "./branch/tabPanels";
import { BranchTabs } from "./branch/BranchTabs";
import { useBranchForm } from "./useBranchForm";

export const BranchForm = ({ initial, initialTab = "basics", onSaved, onCancel, onTabChange }) => {
  const { t } = useTranslation();
  const { locate } = useGeolocation();
  const company = useMyCompany();
  const [tab, setTab] = useState(initialTab);
  const shell = useRef(null);
  const { form, setForm, busy, submit } = useBranchForm(initial, onSaved);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setNested = (group, key, value) => setForm((f) => ({ ...f, [group]: { ...f[group], [key]: value } }));
  const changeTab = (next) => {
    setTab(next);
    onTabChange?.(next);
  };

  useEffect(() => {
    setTab(initialTab);
    onTabChange?.(initialTab);
    shell.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [initial?.id, initialTab]);

  useEffect(() => {
    const { latitude, longitude } = form;
    if (!latitude || !longitude) return;
    let alive = true;
    resolveTimezone(latitude, longitude)
      .then((tz) => alive && setForm((f) => ({ ...f, ...tz })))
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.latitude, form.longitude]);

  const useMyLocation = async () => {
    const c = await locate();
    setForm((f) => ({ ...f, latitude: c.latitude.toFixed(6), longitude: c.longitude.toFixed(6) }));
  };

  const pickLocation = useBranchAddress(form, setForm).pickLocation;

  const props = { form, set, setNested };

  return (
    <div ref={shell} className="scroll-mt-4">
      <NeuCard className="animate-rise-in" data-testid="branch-form">
        <div className="mb-5 pb-4">
          <p className="text-lg font-black" data-testid="branch-form-title">
            {initial?.id ? t("branches.editTitle", { name: initial.name }) : t("branches.add")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">{t("branches.tabsHint")}</p>
        </div>

        <BranchTabs tab={tab} onChange={changeTab} />

        <form onSubmit={submit}>
          <TabPanel tab={tab} props={props} company={company} branchId={initial?.id}
            onLocate={useMyLocation} onPickLocation={pickLocation} />

          <div className="mt-7 flex flex-wrap gap-2 pt-5">
            <NeuButton variant="primary" type="submit" data-testid="branch-save" disabled={busy}>
              {t("app.save")}
            </NeuButton>
            <NeuButton type="button" data-testid="branch-cancel" onClick={onCancel}>
              {t("app.cancel")}
            </NeuButton>
          </div>
        </form>
      </NeuCard>
    </div>
  );
};
